import { memo, useCallback, useMemo, useState } from 'react';
import {
  AppBar as MuiAppBar,
  IconButton as MuiIconButton,
  Toolbar,
  Typography,
  Button,
  colors,
  styled,
  Tooltip,
  Stack,
  Box,
} from '@mui/material';
import { ClearAll, Menu as MenuIcon } from '@mui/icons-material';
import { Settings } from 'react-feather';
import { NavLink, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  HeaderVehicleAlertList,
  HeaderMapVersionAlert,
  HeaderUserMenu,
} from '@components/common/molecules';
import ProjectEnvironmentDialog from '../ProjectEnvrionmentDialog';
import { useTranslation } from 'react-i18next';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import { useFilteredRoutes } from '@routes/states';
import MaintenanceInfo from '../MaintenanceInfo';
import { useSnackbar } from 'notistack';
import { hasAlertAtom } from '@data/notification';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useAtomValue, useSetAtom } from 'jotai';

const AppBar = styled(MuiAppBar)`
  background: ${(props) => props.theme.header.background};
  color: ${(props) => props.theme.header.color};
  box-shadow: ${(props) => props.theme.shadows[1]};
`;

const IconButton = styled(MuiIconButton)`
  svg {
    width: 22px;
    height: 22px;
  }
`;

type HeaderProps = {
  onDrawerToggle: React.MouseEventHandler<HTMLElement>;
};

const Header: React.FC<HeaderProps> = ({ onDrawerToggle }: HeaderProps) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const project = useAtomValue(projectAtom);
  const environment = useAtomValue(environmentAtom);
  const routes = useFilteredRoutes();
  const { closeSnackbar } = useSnackbar();
  const setHasAlert = useSetAtom(hasAlertAtom);

  const currentRoute = useMemo(() => {
    const firstPathName = location.pathname
      .substring(1, location.pathname.length)
      .split('/')[2];
    const currentRoute = routes.find((route) => {
      const routePath = route.path.split('/')[0];
      return routePath === firstPathName;
    });
    return currentRoute;
  }, [location.pathname, routes]);

  const title = useMemo(() => {
    if (isNullOrUndefined(currentRoute)) return null;
    if (currentRoute.id === 'vehicle_condition') {
      if (location.pathname.includes('maintenance')) {
        return t('vehicle_condition.list.condition');
      } else if (location.pathname.includes('parameter')) {
        return t('vehicle_condition.list.individual_parameter');
      }
    }
    if (
      currentRoute.id === 'driving_environment' &&
      location.pathname.includes('no-entry-zone-setting')
    ) {
      return t('driving_environment.no_entry_zone_setting.title');
    }
    return t(`page.${currentRoute.id}`);
  }, [currentRoute, t, location]);

  const handleClickClearAllNotification = useCallback(() => {
    setHasAlert(false);
    closeSnackbar();
  }, [closeSnackbar, setHasAlert]);

  const handleClickDialogButton = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  return (
    <>
      <Helmet title={title ?? undefined} />
      <AppBar position="sticky" elevation={0}>
        <MaintenanceInfo />
        <Toolbar sx={{ width: '100%' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: '100%' }}
          >
            <Stack direction="row" alignItems="center">
              <IconButton
                color="inherit"
                aria-label="Open drawer"
                onClick={onDrawerToggle}
                data-testid="menu-icon"
                sx={{ display: { xs: 'block', xl: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Typography
                variant="subtitle1"
                color="textPrimary"
                display="inline"
                data-testid="header-title"
              >
                {title}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2} mr={14}>
              <SelectedEnvironment data-testid="prjEnvName">
                {project?.display_name} / {environment?.environment_name}
              </SelectedEnvironment>
              <Box>
                <Button
                  color="primary"
                  variant="outlined"
                  sx={{
                    mr: 2,
                    ml: 4,
                  }}
                  onClick={handleClickDialogButton}
                  data-testid="switchEnv-btn"
                >
                  {t('header.switch_environment')}
                </Button>
              </Box>
              <HeaderMapVersionAlert />
              <HeaderVehicleAlertList />
              <Tooltip arrow title={t('notification.clear_all')}>
                <IconButton
                  color="inherit"
                  data-testid="alert-clear-icon"
                  onClick={handleClickClearAllNotification}
                >
                  <ClearAll data-testid="ClearAllIcon" />
                </IconButton>
              </Tooltip>
              <HeaderUserMenu />
              <NavLink
                to={`/${project?.id}/${environment?.environment_id}/settings/`}
              >
                <IconButton
                  sx={{ color: colors.grey[500] }}
                  data-testid="setting-icon"
                >
                  <Settings />
                </IconButton>
              </NavLink>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>
      <ProjectEnvironmentDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        project={project!}
        environment={environment!}
      />
    </>
  );
};

export default memo(Header);

const SelectedEnvironment = styled(Typography)`
  color: ${colors.grey[700]};
  max-width: 900px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
